/** User-configurable dashboard filter store. */

import { create } from 'zustand';
import { persist } from 'zustand/middleware';

export type MomentumLevelThreshold = 'all' | 'medium' | 'high' | 'critical';
export type MomentumAlertType =
  | 'gradual_acceleration'
  | 'quality_surge'
  | 'cross_subreddit_spread'
  | 'dd_mention_combo';

const DEFAULT_BANNED_TICKERS = ['GOOG', 'HYSA', 'USA', 'SPY', 'META', 'DD', 'NVDA', 'AMZN'];
const DEFAULT_MOMENTUM_TYPES: MomentumAlertType[] = [
  'gradual_acceleration',
  'quality_surge',
  'cross_subreddit_spread',
  'dd_mention_combo',
];

interface FilterState {
  velocityBannedTickers: string[];
  momentumBannedTickers: string[];
  serverDefaultVelocityBanTickers: string[];
  initializedFromServer: boolean;
  seedFromServerDefaults: (defaults: string[]) => void;
  addVelocityBannedTicker: (ticker: string) => void;
  removeVelocityBannedTicker: (ticker: string) => void;
  setVelocityBannedTickers: (tickers: string[]) => void;
  clearVelocityBannedTickers: () => void;
  resetVelocityBannedTickersToDefaults: () => void;
  addMomentumBannedTicker: (ticker: string) => void;
  removeMomentumBannedTicker: (ticker: string) => void;
  setMomentumBannedTickers: (tickers: string[]) => void;
  clearMomentumBannedTickers: () => void;
  resetMomentumBannedTickersToDefaults: () => void;
  momentumMinLevel: MomentumLevelThreshold;
  setMomentumMinLevel: (level: MomentumLevelThreshold) => void;
  momentumEnabledTypes: MomentumAlertType[];
  toggleMomentumType: (type: MomentumAlertType, enabled: boolean) => void;
  resetMomentumTypes: () => void;
  hypeMinPrice: number | null;
  hypeMaxPrice: number | null;
  setHypePriceRange: (minPrice: number | null, maxPrice: number | null) => void;
  clearHypePriceRange: () => void;
  crossSignalMinHypeScore: number;
  setCrossSignalMinHypeScore: (value: number) => void;
}

function normalizeTicker(ticker: string): string {
  return ticker.trim().toUpperCase();
}

function normalizeUniqueTickers(tickers: string[]): string[] {
  const normalized = tickers
    .map(normalizeTicker)
    .filter((ticker) => /^[A-Z][A-Z0-9.\-]{0,9}$/.test(ticker));
  return Array.from(new Set(normalized)).sort((a, b) => a.localeCompare(b));
}

export const useFilterStore = create<FilterState>()(
  persist(
    (set, get) => ({
      velocityBannedTickers: [],
      momentumBannedTickers: [],
      serverDefaultVelocityBanTickers: [],
      initializedFromServer: false,
      momentumMinLevel: 'all',
      momentumEnabledTypes: DEFAULT_MOMENTUM_TYPES,
      hypeMinPrice: null,
      hypeMaxPrice: null,
      crossSignalMinHypeScore: 40,
      seedFromServerDefaults: (defaults: string[]) => {
        const normalizedDefaults = normalizeUniqueTickers([...(defaults || []), ...DEFAULT_BANNED_TICKERS]);
        const { initializedFromServer, velocityBannedTickers, momentumBannedTickers } = get();
        if (
          !initializedFromServer &&
          velocityBannedTickers.length === 0 &&
          momentumBannedTickers.length === 0
        ) {
          set({
            velocityBannedTickers: normalizedDefaults,
            momentumBannedTickers: normalizedDefaults,
            serverDefaultVelocityBanTickers: normalizedDefaults,
            initializedFromServer: true,
          });
          return;
        }
        set({ serverDefaultVelocityBanTickers: normalizedDefaults, initializedFromServer: true });
      },
      addVelocityBannedTicker: (ticker: string) =>
        set((state) => {
          const next = normalizeUniqueTickers([...state.velocityBannedTickers, ticker]);
          return { velocityBannedTickers: next };
        }),
      removeVelocityBannedTicker: (ticker: string) =>
        set((state) => ({
          velocityBannedTickers: state.velocityBannedTickers.filter(
            (item) => item !== normalizeTicker(ticker)
          ),
        })),
      setVelocityBannedTickers: (tickers: string[]) =>
        set({ velocityBannedTickers: normalizeUniqueTickers(tickers) }),
      clearVelocityBannedTickers: () => set({ velocityBannedTickers: [] }),
      resetVelocityBannedTickersToDefaults: () =>
        set((state) => ({ velocityBannedTickers: [...state.serverDefaultVelocityBanTickers] })),
      addMomentumBannedTicker: (ticker: string) =>
        set((state) => {
          const next = normalizeUniqueTickers([...state.momentumBannedTickers, ticker]);
          return { momentumBannedTickers: next };
        }),
      removeMomentumBannedTicker: (ticker: string) =>
        set((state) => ({
          momentumBannedTickers: state.momentumBannedTickers.filter(
            (item) => item !== normalizeTicker(ticker)
          ),
        })),
      setMomentumBannedTickers: (tickers: string[]) =>
        set({ momentumBannedTickers: normalizeUniqueTickers(tickers) }),
      clearMomentumBannedTickers: () => set({ momentumBannedTickers: [] }),
      resetMomentumBannedTickersToDefaults: () =>
        set((state) => ({ momentumBannedTickers: [...state.serverDefaultVelocityBanTickers] })),
      setMomentumMinLevel: (level) => set({ momentumMinLevel: level }),
      toggleMomentumType: (type: MomentumAlertType, enabled: boolean) =>
        set((state) => {
          const current = new Set(state.momentumEnabledTypes);
          if (enabled) {
            current.add(type);
          } else {
            current.delete(type);
          }
          const next = DEFAULT_MOMENTUM_TYPES.filter((item) => current.has(item));
          return { momentumEnabledTypes: next };
        }),
      resetMomentumTypes: () => set({ momentumEnabledTypes: DEFAULT_MOMENTUM_TYPES }),
      setHypePriceRange: (minPrice, maxPrice) =>
        set({
          hypeMinPrice: minPrice,
          hypeMaxPrice: maxPrice,
        }),
      clearHypePriceRange: () => set({ hypeMinPrice: null, hypeMaxPrice: null }),
      setCrossSignalMinHypeScore: (value: number) =>
        set({ crossSignalMinHypeScore: Math.max(0, Math.min(100, Number(value) || 0)) }),
    }),
    {
      name: 'tkrscan-filter-store-v2',
      partialize: (state) => ({
        velocityBannedTickers: state.velocityBannedTickers,
        momentumBannedTickers: state.momentumBannedTickers,
        serverDefaultVelocityBanTickers: state.serverDefaultVelocityBanTickers,
        initializedFromServer: state.initializedFromServer,
        momentumMinLevel: state.momentumMinLevel,
        momentumEnabledTypes: state.momentumEnabledTypes,
        hypeMinPrice: state.hypeMinPrice,
        hypeMaxPrice: state.hypeMaxPrice,
        crossSignalMinHypeScore: state.crossSignalMinHypeScore,
      }),
    }
  )
);

